<template>
  <span class="breadcrumb-choice">
    <component
      :is="to ? 'a' : 'button'"
      v-bind="activatorBindings"
      :href="to ? router.resolve(to).href : undefined"
      :type="to ? undefined : 'button'"
      :title="to ? t('navigation.holdToChooseEvent') : label"
      class="breadcrumb-choice__label"
      @pointerdown="startHold"
      @pointermove="moveHold"
      @pointerup="cancelHold"
      @pointercancel="cancelHold"
      @pointerleave="cancelHold"
      @contextmenu.prevent="contextMenu"
      @click="click"
      @keydown.down.prevent="openMenu"
      @keydown.space.prevent="openMenu"
      @dragstart.prevent="cancelHold"
    >{{ label }}</component>
    <button type="button" class="breadcrumb-choice__arrow" :aria-label="menuLabel" aria-haspopup="menu" :aria-expanded="menuProps?.['aria-expanded'] === true || menuProps?.['aria-expanded'] === 'true'" @click="openMenu">
      <v-icon icon="mdi-chevron-down" size="16" aria-hidden="true" />
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed, onScopeDispose } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
const props = defineProps<{ label: string; menuLabel: string; to?: RouteLocationRaw; menuProps?: Record<string, unknown> }>()
const emit = defineEmits<{ open: [] }>()
const router = useRouter(), { t } = useI18n()
const activatorBindings = computed(() => {
  const { onClick, ...bindings } = props.menuProps || {}
  return bindings
})
let timer: ReturnType<typeof setTimeout> | undefined
let held = false, originX = 0, originY = 0
function cancelHold() { clearTimeout(timer); timer = undefined }
function contextMenu() { held = true; openMenu() }
function openMenu() { cancelHold(); emit('open') }
function startHold(event: PointerEvent) {
  cancelHold()
  held = false
  if (!event.isPrimary || event.button !== 0) return
  originX = event.clientX; originY = event.clientY
  timer = setTimeout(() => { held = true; openMenu() }, 500)
}
function moveHold(event: PointerEvent) {
  if (Math.hypot(event.clientX - originX, event.clientY - originY) > 8) cancelHold()
}
function click(event: MouseEvent) {
  if (held) { event.preventDefault(); held = false; return }
  if (!props.to) { openMenu(); return }
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return
  event.preventDefault()
  router.push(props.to)
}
onScopeDispose(cancelHold)
</script>

<style scoped>
.breadcrumb-choice { display: inline-flex; align-items: center; color: var(--cs-accent); }
.breadcrumb-choice__label, .breadcrumb-choice__arrow { color: inherit; font: inherit; background: transparent; border: 0; border-radius: 6px; cursor: pointer; min-height: 36px; padding: 6px; text-decoration: none; }
.breadcrumb-choice__label { user-select: none; -webkit-touch-callout: none; touch-action: pan-x pan-y; }
.breadcrumb-choice__arrow { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; }
.breadcrumb-choice__label:hover, .breadcrumb-choice__arrow:hover { background: var(--cs-selection); }
.breadcrumb-choice :focus-visible { outline: 2px solid var(--cs-action); outline-offset: -2px; }
@media(max-width:600px) { .breadcrumb-choice__label, .breadcrumb-choice__arrow { min-height: 44px; } }
</style>
